import React, { useCallback, useEffect, useState } from "react";
import { api, formatError } from "@/lib/api";
import { toast } from "@/lib/toast";
import { AlertTriangle, CheckCircle2 } from "lucide-react";

function warnDate(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  if (isNaN(d)) return "";
  return d.toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" });
}

// A real, member-visible record of any moderation warning issued to this account (paired with the
// warning email). Renders nothing when the member has no warnings. Scoped to the signed-in user by
// the /me/warnings endpoint, so it's always the viewer's OWN warnings.
export default function MemberWarningsBanner({ className = "" }) {
  const [warnings, setWarnings] = useState([]);
  const [busyId, setBusyId] = useState(null);

  const load = useCallback(() => {
    api.get("/me/warnings").then(({ data }) => setWarnings(data.warnings || [])).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  const acknowledge = async (id) => {
    setBusyId(id);
    try {
      await api.post(`/me/warnings/${id}/acknowledge`);
      setWarnings((ws) => ws.map((w) => (w.warning_id === id ? { ...w, acknowledged: true } : w)));
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail) || "Couldn't update that.");
    } finally { setBusyId(null); }
  };

  if (warnings.length === 0) return null;

  return (
    <div className={`border border-blaze/50 bg-blaze/5 ${className}`} data-testid="member-warnings-banner">
      <div className="flex items-center gap-2 px-4 py-3 border-b border-blaze/30">
        <AlertTriangle className="w-5 h-5 text-blaze shrink-0" />
        <h2 className="font-heading text-lg uppercase tracking-tight text-blaze" data-testid="member-warnings-title">
          Account Warnings ({warnings.length})
        </h2>
      </div>
      <div className="divide-y divide-blaze/20">
        {warnings.map((w) => (
          <div key={w.warning_id} className="px-4 py-3.5" data-testid={`member-warning-${w.warning_id}`}>
            <div className="flex items-start justify-between gap-3 flex-wrap">
              <div className="min-w-0 flex-1">
                <p className="font-heading uppercase text-sm tracking-tight text-foreground" data-testid={`member-warning-summary-${w.warning_id}`}>
                  {w.summary || "Content that violates our community rules"}
                </p>
                {w.detail && <p className="font-body text-sm text-secondary-foreground mt-1 break-words" data-testid={`member-warning-detail-${w.warning_id}`}>{w.detail}</p>}
                <p className="font-mono text-[10px] uppercase text-muted-foreground mt-1.5">
                  Issued {warnDate(w.created_at)}{w.acknowledged ? " · acknowledged" : ""}
                </p>
              </div>
              {!w.acknowledged && (
                <button onClick={() => acknowledge(w.warning_id)} disabled={busyId === w.warning_id}
                  data-testid={`member-warning-ack-${w.warning_id}`}
                  className="inline-flex items-center gap-1.5 border border-blaze/60 text-blaze hover:bg-blaze/10 disabled:opacity-50 font-mono uppercase text-[11px] px-3 py-2 transition-colors shrink-0">
                  <CheckCircle2 className="w-3.5 h-3.5" /> I understand
                </button>
              )}
            </div>
          </div>
        ))}
      </div>
      <p className="px-4 py-2.5 border-t border-blaze/20 font-body text-[12px] text-secondary-foreground">
        Repeated violations of our community rules can lead to your account being suspended.
      </p>
    </div>
  );
}
